/**
 * Enhanced GenAI Live Client combining Google's direct approach with Flowcus robustness
 * @license Apache-2.0
 */

import {
  Content,
  GoogleGenAI,
  LiveCallbacks,
  LiveClientToolResponse,
  LiveConnectConfig,
  LiveServerContent,
  LiveServerMessage,
  LiveServerToolCall,
  LiveServerToolCallCancellation,
  Part,
  Session,
} from "@google/genai";

import { EventEmitter } from "eventemitter3";
import { LiveClientOptions, StreamingLog } from "../types";
import { SaaSUsageMetrics } from "./cost-tracker";
import {
  createAudioContexts,
  createGainNodes,
  createBlob,
  decodeAudioData,
  decode
} from "./enhanced-audio-utils";

/**
 * Enhanced event types with audio visualization support
 */
export interface EnhancedLiveClientEventTypes {
  // Core events
  audio: (data: ArrayBuffer) => void;
  close: (event: CloseEvent) => void;
  content: (data: LiveServerContent) => void;
  error: (error: ErrorEvent) => void;
  interrupted: () => void;
  log: (log: StreamingLog) => void;
  open: () => void;
  setupcomplete: () => void;
  toolcall: (toolCall: LiveServerToolCall) => void;
  toolcallcancellation: (toolcallCancellation: LiveServerToolCallCancellation) => void;
  turncomplete: () => void;
  usage: (usage: SaaSUsageMetrics) => void;
  
  // Enhanced events for visualization
  inputaudio: (volume: number) => void;
  outputaudio: (volume: number) => void;
  audiocontexts: (contexts: { input: AudioContext; output: AudioContext }) => void;
  audionodes: (nodes: { input: GainNode; output: GainNode }) => void;
}

/**
 * Enhanced GenAI Live Client with direct audio processing and visualization support
 */
export class EnhancedGenAILiveClient extends EventEmitter<EnhancedLiveClientEventTypes> {
  protected client: GoogleGenAI;

  private _status: "connected" | "disconnected" | "connecting" = "disconnected";
  public get status() {
    return this._status;
  }

  private _session: Session | null = null;
  public get session() {
    return this._session;
  }

  private _model: string | null = null;
  public get model() {
    return this._model;
  }

  protected config: LiveConnectConfig | null = null;

  // Audio system - Google's approach
  private inputAudioContext: AudioContext | null = null;
  private outputAudioContext: AudioContext | null = null;
  private inputNode: GainNode | null = null;
  private outputNode: GainNode | null = null;
  private nextStartTime = 0;
  private mediaStream: MediaStream | null = null;
  private scriptProcessorNode: ScriptProcessorNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private sources = new Set<AudioBufferSourceNode>();
  private isRecording = false;

  public getConfig() {
    return { ...this.config };
  }

  constructor(options: LiveClientOptions) {
    super();
    this.client = new GoogleGenAI(options);
    this.initAudio();
  }

  /**
   * Initialize audio contexts and nodes
   */
  private async initAudio() {
    try {
      const { inputContext, outputContext } = await createAudioContexts();
      this.inputAudioContext = inputContext;
      this.outputAudioContext = outputContext;

      const { inputNode, outputNode } = createGainNodes(inputContext, outputContext);
      this.inputNode = inputNode;
      this.outputNode = outputNode;

      this.nextStartTime = this.outputAudioContext.currentTime;

      // Emit contexts and nodes for visualization
      this.emit("audiocontexts", {
        input: this.inputAudioContext,
        output: this.outputAudioContext
      });
      this.emit("audionodes", {
        input: this.inputNode,
        output: this.outputNode
      });
    } catch (error) {
      console.error("Failed to initialize audio:", error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.log("audio.error", `Failed to initialize audio: ${message}`);
    }
  }

  protected log(type: string, message: StreamingLog["message"]) {
    const log: StreamingLog = {
      date: new Date(),
      type,
      message,
    };
    this.emit("log", log);
  }

  async connect(
    model: string,
    config: LiveConnectConfig & { token?: string }
  ): Promise<boolean> {
    if (this._status === "connected" || this._status === "connecting") {
      return false;
    }

    this._status = "connecting";
    this.config = config;
    this._model = model;

    const callbacks: LiveCallbacks = {
      onopen: this.onopen.bind(this),
      onmessage: this.onmessage.bind(this),
      onerror: this.onerror.bind(this),
      onclose: this.onclose.bind(this),
    };

    try {
      let clientForConnection = this.client;
      if (config.token) {
        clientForConnection = new GoogleGenAI({ apiKey: config.token });
      }

      this._session = await clientForConnection.live.connect({
        model,
        config,
        callbacks,
      });
    } catch (e) {
      console.error("Error connecting to GenAI Live:", e);
      this._status = "disconnected";
      return false;
    }

    this._status = "connected";
    return true;
  }

  public disconnect() {
    this.stopRecording();
    this.stopAllAudio();
    
    if (!this.session) {
      return false;
    }
    this.session?.close();
    this._session = null;
    this._status = "disconnected";

    this.log("client.close", `Disconnected`);
    return true;
  }

  /**
   * Start audio recording using Google's approach
   */
  public async startRecording(): Promise<boolean> {
    if (this.isRecording || !this.inputAudioContext || !this.inputNode) {
      return false;
    }

    try {
      await this.inputAudioContext.resume();
      
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      this.sourceNode = this.inputAudioContext.createMediaStreamSource(
        this.mediaStream,
      );
      this.sourceNode.connect(this.inputNode);

      const bufferSize = 256;
      this.scriptProcessorNode = this.inputAudioContext.createScriptProcessor(
        bufferSize,
        1,
        1,
      );

      this.scriptProcessorNode.onaudioprocess = (audioProcessingEvent) => {
        if (!this.isRecording) return;

        const inputBuffer = audioProcessingEvent.inputBuffer;
        const pcmData = inputBuffer.getChannelData(0);

        // Calculate volume for visualization
        let volume = 0;
        for (let i = 0; i < pcmData.length; i++) {
          volume += Math.abs(pcmData[i]);
        }
        volume = volume / pcmData.length;
        this.emit("inputaudio", volume);

        // Send to Gemini
        if (this.session) {
          this.session.sendRealtimeInput({ media: createBlob(pcmData) });
        }
      };

      this.sourceNode.connect(this.scriptProcessorNode);
      this.scriptProcessorNode.connect(this.inputAudioContext.destination);

      this.isRecording = true;
      this.log("audio.recording", "Recording started");
      return true;
    } catch (error) {
      console.error('Error starting recording:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.log("audio.error", `Recording failed: ${message}`);
      this.stopRecording();
      return false;
    }
  }

  /**
   * Stop audio recording
   */
  public stopRecording() {
    if (!this.isRecording) return;

    this.isRecording = false;

    if (this.scriptProcessorNode && this.sourceNode) {
      this.scriptProcessorNode.disconnect();
      this.sourceNode.disconnect();
    }

    this.scriptProcessorNode = null;
    this.sourceNode = null;

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    this.log("audio.recording", "Recording stopped");
  }

  /**
   * Stop all audio sources
   */
  private stopAllAudio() {
    for (const source of this.sources.values()) {
      source.stop();
      this.sources.delete(source);
    }
    this.nextStartTime = 0;
  }

  protected onopen() {
    this.log("client.open", "Connected");
    this.emit("open");
  }

  protected onerror(e: ErrorEvent) {
    this.log("server.error", e.message);
    this.emit("error", e);
  }

  protected onclose(e: CloseEvent) {
    this.log(
      `server.close`,
      `disconnected ${e.reason ? `with reason: ${e.reason}` : ``}`
    );
    this.emit("close", e);
  }

  protected async onmessage(message: LiveServerMessage) {
    if (message.setupComplete) {
      this.log("server.send", "setupComplete");
      this.emit("setupcomplete");
      return;
    }
    if (message.toolCall) {
      this.log("server.toolCall", message);
      this.emit("toolcall", message.toolCall);
      return;
    }
    if (message.toolCallCancellation) {
      this.log("server.toolCallCancellation", message);
      this.emit("toolcallcancellation", message.toolCallCancellation);
      return;
    }

    if (message.serverContent) {
      const { serverContent } = message;
      
      if ("interrupted" in serverContent) {
        this.log("server.content", "interrupted");
        this.stopAllAudio();
        this.emit("interrupted");
        return;
      }
      
      if ("turnComplete" in serverContent) {
        this.log("server.content", "turnComplete");
        this.emit("turncomplete");
      }

      if ("modelTurn" in serverContent) {
        let parts: Part[] = serverContent.modelTurn?.parts || [];

        // Handle audio parts with Google's direct approach
        const audioParts = parts.filter(
          (p) => p.inlineData && p.inlineData.mimeType?.startsWith("audio/pcm")
        );

        for (const audioPart of audioParts) {
          const audioData = audioPart.inlineData?.data;
          if (audioData && this.outputAudioContext && this.outputNode) {
            await this.playAudioData(audioData);
          }
        }

        // Handle non-audio content
        const otherParts = parts.filter(
          (p) => !p.inlineData || !p.inlineData.mimeType?.startsWith("audio/pcm")
        );

        if (otherParts.length > 0) {
          const content: { modelTurn: Content } = { modelTurn: { parts: otherParts } };
          this.emit("content", content);
          this.log(`server.content`, message);
        }
      }
    } else {
      console.log("received unmatched message", message);
    }
  }

  /**
   * Play audio data using Google's direct scheduling approach
   */
  private async playAudioData(base64Data: string) {
    if (!this.outputAudioContext || !this.outputNode) return;

    try {
      this.nextStartTime = Math.max(
        this.nextStartTime,
        this.outputAudioContext.currentTime,
      );

      const audioBuffer = await decodeAudioData(
        decode(base64Data),
        this.outputAudioContext,
        24000,
        1,
      );

      const source = this.outputAudioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.outputNode);

      source.addEventListener('ended', () => {
        this.sources.delete(source);
      });

      // Calculate volume for visualization
      const channelData = audioBuffer.getChannelData(0);
      let volume = 0;
      for (let i = 0; i < channelData.length; i += 100) {
        volume += Math.abs(channelData[i]);
      }
      volume = volume / (channelData.length / 100);
      this.emit("outputaudio", volume);

      source.start(this.nextStartTime);
      this.nextStartTime = this.nextStartTime + audioBuffer.duration;
      this.sources.add(source);

      // Emit raw audio data for legacy compatibility
      const arrayBuffer = decode(base64Data).buffer as ArrayBuffer;
      this.emit("audio", arrayBuffer);
      this.log(`server.audio`, `buffer (${arrayBuffer.byteLength})`);
    } catch (error) {
      console.error("Error playing audio:", error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.log("audio.error", `Playback failed: ${message}`);
    }
  }

  /**
   * Send realtime input
   */
  sendRealtimeInput(chunks: Array<{ mimeType: string; data: string }>) {
    let hasAudio = false;
    let hasVideo = false;
    for (const ch of chunks) {
      this.session?.sendRealtimeInput({ media: ch });
      if (ch.mimeType.includes("audio")) {
        hasAudio = true;
      }
      if (ch.mimeType.includes("image")) {
        hasVideo = true;
      }
      if (hasAudio && hasVideo) {
        break;
      }
    }
    const message =
      hasAudio && hasVideo
        ? "audio + video"
        : hasAudio
        ? "audio"
        : hasVideo
        ? "video"
        : "unknown";
    this.log(`client.realtimeInput`, message);
  }

  /**
   * Send tool response
   */
  sendToolResponse(toolResponse: LiveClientToolResponse) {
    if (
      toolResponse.functionResponses &&
      toolResponse.functionResponses.length
    ) {
      this.session?.sendToolResponse({
        functionResponses: toolResponse.functionResponses,
      });
      this.log(`client.toolResponse`, toolResponse);
    }
  }

  /**
   * Send normal content parts
   */
  send(parts: Part | Part[], turnComplete: boolean = true) {
    this.session?.sendClientContent({ turns: parts, turnComplete });
    this.log(`client.send`, {
      turns: Array.isArray(parts) ? parts : [parts],
      turnComplete,
    });
  }

  /**
   * Get audio contexts for visualization
   */
  public getAudioContexts() {
    return {
      input: this.inputAudioContext,
      output: this.outputAudioContext
    };
  }

  /**
   * Get audio nodes for visualization
   */
  public getAudioNodes() {
    return {
      input: this.inputNode,
      output: this.outputNode
    };
  }
}